import React, { useRef, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import axios from "axios";
import jsonpAdapter from "axios-jsonp";
import { Box, Typography, Skeleton, Link } from "@mui/material";
import { makeStyles } from "@mui/styles";
import ModalView from "../ModalView/ModalView";
import ClientConfigurationHelper from "../../configuration/ClientConfigurationHelper";
import CarouselItem from "./CarouselItem";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const useStyles = makeStyles((theme) => {
    const arrowSizeValue = theme.spacing(5);
    const arrowBackgroundColorValue = "#f5f5f5";
    return {
        wrapper: {
            "&:focus-visible": {
                outline: "none",
            },
            "& > div:focus-visible": {
                outline: "none",
            },
            "& .swiper": {
                overflow: "hidden",
            },
        },
        fade: {},
        arrows: {
            marginTop: `calc(0px - (${arrowSizeValue}/2))`,
            overflow: "hidden",
            width: arrowSizeValue,
            height: arrowSizeValue,
            backgroundColor: arrowBackgroundColorValue,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            zIndex: 9,
            justifyContent: "center",
            opacity: 1,
            "&:after": {
                display: "none",
            },
            "& div": {
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
            },
        },
        hoverArrow: {
            backgroundColor: arrowBackgroundColorValue,
            opacity: 1,
        },
    };
});

const CarouselWidget = () => {
    const channelId = ClientConfigurationHelper.getLatestVideosChannelId();
    const clientId = TVSite.loginId;
    const [content, setContent] = useState(null);
    const [items, setItems] = useState([]);
    const [initialized, setInitialized] = useState(false);
    const [swiperInstance, setSwiperInstance] = useState(null);
    const [page, setPage] = useState(0);
    const [keepLoading, setKeepLoading] = useState(false);
    const [activePosition, setActivePosition] = useState(2);
    const [isHoveringPrev, setIsHoveringPrev] = useState(false);
    const [isHoveringNext, setIsHoveringNext] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [removeHeigh, setRemoveHeigh] = useState(false);
    const [loading, setLoading] = useState(true);
    const classes = useStyles();

    const arrowL = useRef(null);
    const arrowR = useRef(null);

    const getNext = () => {
        const newItem = activePosition + 1;
        if (items[newItem]) {
            setActivePosition(newItem);
            swiperInstance.slideTo(newItem, 500);
        }
    };

    const getPrev = () => {
        const newIndex = activePosition - 1;
        const newItem = newIndex < 0 ? 0 : newIndex;
        if (items[newItem]) {
            setActivePosition(newItem);
            swiperInstance.slideTo(newItem, 500);
        }
    };

    const getEnv = () => {
        const phpEnvName = window?.TVSite?.phpEnvName;
        const env = phpEnvName === "local" ? "test" : phpEnvName;
        if (env) {
            const isProduction = env === "prod" || env === "app";
            return isProduction ? "app" : env;
        }
        return "app";
    };

    const setVideoProducts = async (videos) => {
        const idsArray = videos.map((item) => item.id);
        const requestQuery = `
        {
          content_by_ids(content_ids: [${idsArray}] ) {
            content_id
            creator{
                last_name
                full_name
                thumbnail
                first_name
                uri_stem
            }
            products{
                title
                image
                sale_price
                price
                link
                description
                reference_id
                product_id
            }
          }
        }`;

        try {
            const response = await axios.post(
                ClientConfigurationHelper.getContentSearchUrl(),
                { query: requestQuery },
                { withCredentials: true },
            );

            const {
                data: {
                    data: { content_by_ids },
                },
            } = response;

            const updatedVideos = videos.map((video) => {
                const matchedContent = content_by_ids.find(
                    (content) => content.content_id === video.id,
                );
                if (matchedContent) {
                    video.products = matchedContent.products || [];
                    video.creator = matchedContent.creator || [];
                }
                return video;
            });

            return updatedVideos;
        } catch (error) {
            console.log("error loading items");
            return videos;
        }
    };

    useEffect(() => {
        if (initialized) {
            return;
        }

        (function () {
            window._tvpa = window._tvpa || [];
            const analyticsConfig = {
                li: `${clientId}`,
                logUrl: "https://api.tvpage.com/api/__tvpa.gif",
            };
            window._tvpa.push(["config", analyticsConfig]);
        })();

        let url = `https://${getEnv()}.tvpage.com/api/channels/${channelId}/videos?X-login-id=${clientId}&status=approved&n=24`;

        axios({
            url: url,
            adapter: jsonpAdapter,
        })
            .then(async (response) => {
                const data = response.data;
                setKeepLoading(data?.length === 24);
                const updatedVideos = await setVideoProducts(data);
                setItems(updatedVideos);
                setInitialized(true);
                setLoading(false);
            })
            .catch((error) => {
                setInitialized(true);
                setLoading(false);
                console.log(error);
            });
    }, [channelId, clientId]);

    useEffect(() => {
        if (initialized && keepLoading && page > 0) {
            let url = `https://${getEnv()}.creatable.io/api/channels/${channelId}/contents?X-login-id=${clientId}&status=approved&n=24&p=${page}`;
            axios({
                url: url,
                adapter: jsonpAdapter,
            })
                .then(async (response) => {
                    const data = response.data;
                    setKeepLoading(data?.length === 24);
                    const updatedVideos = await setVideoProducts(data);
                    setItems((prevState) => prevState.concat(updatedVideos));
                })
                .catch((error) => console.log(error));
        }
    }, [page, keepLoading, initialized]);

    useEffect(() => {
        if (items?.length > 0) {
            let loadedImages = 0;
            items.forEach((item) => {
                const img = new Image();
                img.src = item.asset.thumbnailUrl;
                img.onload = () => {
                    loadedImages += 1;
                    if (loadedImages === items.length) {
                        setTimeout(() => {
                            setRemoveHeigh(true);
                        }, 1000);
                    }
                };
            });
        }
    }, [items]);

    const onClickItem = ({ index, item }) => {
        setActivePosition(index);
        setContent(item);
        setOpenModal(true);
    };

    const onCloseModal = () => {
        setOpenModal(false);
    };

    const hasData = items?.length > 0;

    if (loading || !hasData) {
        return (
            <Box pb={5} pt={2}>
                <Box mb={4}>
                    <Typography fontSize={{ xs: "28px", sm: "32px" }} fontWeight={700} variant={"h4"}>
                        Trending
                    </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                    {window.innerWidth >= 768 && (
                        <div className={classes.arrows}>
                            <ChevronLeftIcon htmlColor={"#979797"} />
                        </div>
                    )}
                    {Array.from({
                        length: window.innerWidth >= 768 ? 4 : 1,
                    }).map((_, index) => (
                        <Box
                            key={index}
                            width={{ xs: "100%", md: "22%" }}
                            position="relative"
                            sx={{
                                paddingTop: window.innerWidth >= 768 ? "22.56%" : "75%",
                            }}
                        >
                            <Skeleton
                                variant="rectangular"
                                animation="wave"
                                sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                }}
                            />
                        </Box>
                    ))}
                    {window.innerWidth >= 768 && (
                        <div className={classes.arrows}>
                            <ChevronRightIcon htmlColor={"#979797"} />
                        </div>
                    )}
                </Box>
            </Box>
        );
    }

    return (
        <Box pb={5} pt={2}>
            <Box mb={4}>
                <Typography fontSize={{ xs: "28px", sm: "32px" }} fontWeight={700} variant={"h4"}>
                    Trending
                </Typography>
            </Box>
            <Box
                mx={{ xs: 0, sm: 5 }}
                className={classes.wrapper}
                position={"relative"}
                style={{ "--swiper-navigation-sides-offset": "-48px" }}
                sx={{
                    minHeight: {
                        xs: !removeHeigh ? "400px" : "unset",
                        md: !removeHeigh ? "260px" : "unset",
                    },
                }}
            >
                <Swiper
                    observer={true}
                    centerInsufficientSlides={true}
                    breakpoints={{
                        600: {
                            //navigation: true,
                            slidesPerView: 1.4,
                            spaceBetween: 16,
                        },
                        769: {
                            //navigation: true,
                            slidesPerView: 4.2,
                            spaceBetween: 16,
                        },
                    }}
                    style={{
                        "--swiper-navigation-top-offset": "50%",
                        "--swiper-navigation-sides-offset": "-48px",
                    }}
                    onInit={(swiper) => {
                        setSwiperInstance(swiper);
                    }}
                    className={"content-portrait-swiper"}
                    navigation={{
                        enabled: false,
                        nextEl: arrowR.current,
                        prevEl: arrowL.current,
                    }}
                    onReachEnd={() => {
                        setPage((prevState) => prevState + 1);
                    }}
                    autoHeight={true}
                    modules={[Navigation]}
                    slidesPerView={1.4}
                    spaceBetween={16}
                >
                    {items.map((item, index) => {
                        return (
                            <SwiperSlide>
                                <CarouselItem
                                    asset={item}
                                    onClickItem={onClickItem}
                                    index={index}
                                />
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
                <Box
                    display={{ xs: "none", sm: "flex" }}
                    data-testId={"carousel-widget-prev-arrow"}
                    onMouseEnter={() => {
                        setIsHoveringPrev(true);
                    }}
                    onMouseLeave={() => setIsHoveringPrev(false)}
                    onTouchStart={() => {
                        setIsHoveringPrev(true);
                    }}
                    onTouchEnd={() => {
                        setTimeout(() => {
                            setIsHoveringPrev(false);
                        }, 200);
                    }}
                    onClick={() => swiperInstance.slidePrev()}
                    className={
                        "swiper-button-prev " +
                        (isHoveringPrev ? classes.hoverArrow + " " : "") +
                        classes.arrows
                    }
                >
                    <div>
                        <ChevronLeftIcon htmlColor={"#000000"} />
                    </div>
                </Box>
                <Box
                    data-testId={"carousel-widget-next-arrow"}
                    display={{ xs: "none", sm: "flex" }}
                    onClick={() => swiperInstance.slideNext()}
                    onMouseEnter={() => {
                        setIsHoveringNext(true);
                    }}
                    onMouseLeave={() => setIsHoveringNext(false)}
                    onTouchStart={() => {
                        setIsHoveringNext(true);
                    }}
                    onTouchEnd={() => {
                        setTimeout(() => {
                            setIsHoveringNext(false);
                        }, 200);
                    }}
                    className={
                        "swiper-button-next " +
                        (isHoveringNext ? classes.hoverArrow + " " : "") +
                        classes.arrows
                    }
                >
                    <div>
                        <ChevronRightIcon htmlColor={"#000000"} />
                    </div>
                </Box>
                <ModalView
                    autoplay={true}
                    closeModal={onCloseModal}
                    videos={items}
                    activePosition={activePosition}
                    modal={{ position: activePosition, open: openModal, content: content }}
                    getPrev={getPrev}
                    getNext={getNext}
                    clientId={clientId}
                />
            </Box>
        </Box>
    );
};

export default CarouselWidget;
